import React, { useContext, useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import InputMask from 'react-input-mask';
import classNames from 'classnames';
import { isValid, parse } from 'date-fns';
import en from 'date-fns/locale/en-US';
import fr from 'date-fns/locale/fr';

import FormFieldError from './FormFieldError';
import FormFieldLabel from './FormFieldLabel';
import FormFieldTooltip, { TooltipSource } from './FormFieldTooltip';
import { FormProvider } from './FormProvider';
import useFormField, { FieldEvent, FieldWidth } from './useFormField';
import { formatDate, getPlaceholderFromDateFormat } from './utils';

registerLocale('en', en);
registerLocale('fr', fr);

interface IProps {
  name: string;
  label?: string;
  tooltip?: TooltipSource;
  description?: string;
  disabled?: boolean;
  width?: FieldWidth;
  minDate?: Date;
  maxDate?: Date;
  onFocus?: FieldEvent<Date | null>;
  onChange?: FieldEvent<Date | null>;
  onBlur?: FieldEvent<Date | null>;
}

function FormDateField({
  name,
  label,
  tooltip,
  description,
  disabled,
  width,
  minDate,
  maxDate,
  onFocus,
  onChange,
  onBlur,
}: IProps) {
  const ref = useRef(null);
  const isInitialMount = useRef(true);
  const { locale, dateFormat, calendarStartDay } = useContext(FormProvider);

  const {
    id,
    fieldProps,
    inputProps,
    setFieldValue,
    setFieldTouched,
    required,
    disabled: formDisabled,
  } = useFormField<Date | null>(name, width, { onFocus, onChange, onBlur });
  const [isShowingDatePicker, setShowingDatePicker] = useState(false);
  const [value, setValue] = useState(formatDate(inputProps.value, dateFormat));

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const dateParsed = parse(value, dateFormat, new Date());
    if (isValidDate(dateParsed)) {
      setFieldValue(name, dateParsed);
    } else {
      setFieldValue(name, null);
    }
  }, [value]);

  const isValidDate = (date: Date): boolean => {
    return (
      isValid(date) &&
      (!minDate || date.getTime() >= minDate.getTime()) &&
      (!maxDate || date.getTime() <= maxDate.getTime())
    );
  };

  const onBlurHandler = () => setFieldTouched(name, true);
  const onInputChange = (event) => {
    if (event.type === 'change') {
      setValue(event.target.value);
    }
  };
  const onDateChange = (date) => {
    setValue(formatDate(date, dateFormat));
    setShowingDatePicker(false);
    ref.current.focus();
  };

  const isDisabled = formDisabled || disabled;
  const placeholder = getPlaceholderFromDateFormat(dateFormat, locale);
  const className = classNames('novoInputWithButton', { 'novoInputWithButton--disabled': isDisabled });
  return (
    <div {...fieldProps} className={fieldProps.className}>
      {label && (
        <div className="novoFormField__name">
          <FormFieldLabel for={id} required={required}>
            {label}
          </FormFieldLabel>
          <FormFieldTooltip source={tooltip} />
        </div>
      )}
      {description && <p className="novoFormField__description">{description}</p>}
      <div className="novoInputDate">
        <div className={className} onFocus={inputProps.onFocus} onBlur={onBlurHandler}>
          <InputMask
            id={id}
            ref={ref}
            mask={dateFormat.replace(/[a-z]/gi, '9')}
            maskPlaceholder={placeholder}
            placeholder={placeholder}
            className="novoFormField__input"
            disabled={isDisabled}
            value={value}
            onChange={onInputChange}
            required={required}
          />
          <button
            type="button"
            className="material-icons novoInputWithButton__button"
            disabled={isDisabled}
            tabIndex={-1}
            onClick={() => setShowingDatePicker(!isShowingDatePicker)}
          >
            date_range
          </button>
        </div>
        {isShowingDatePicker && (
          <div className="novoDatePicker">
            <div className="novoDatePicker__arrow" />
            <DatePicker
              inline
              disabled={isDisabled}
              autoComplete="off"
              dateFormat={dateFormat}
              locale={locale}
              minDate={minDate}
              maxDate={maxDate}
              showYearDropdown
              dropdownMode={'select'}
              selected={(inputProps.value && new Date(inputProps.value)) || null}
              onChange={onDateChange}
              onClickOutside={() => setShowingDatePicker(false)}
              calendarStartDay={calendarStartDay}
            />
          </div>
        )}
      </div>
      <FormFieldError name={name} />
    </div>
  );
}

export default FormDateField;
